/* JT page sections: header ornament, questions, forms, events, and the little machines */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const once = (el, key) => {
  if (el.dataset[key]) return false;
  el.dataset[key] = '1';
  return true;
};
const confettiAt = (el) => {
  if (!window.JT || typeof window.JT.confetti !== 'function' || !el) return;
  const r = el.getBoundingClientRect();
  window.JT.confetti(r.left + r.width / 2, r.top + r.height / 2);
};
const restartClass = (el, cls, ms) => {
  el.classList.remove(cls);
  void el.getBoundingClientRect();
  el.classList.add(cls);
  if (ms) setTimeout(() => el.classList.remove(cls), ms);
};
const todayLocal = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};
const parseDay = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};

/* Page header ornament: tap for a spin and a little confetti */
function initOrnaments(scope) {
  scope.querySelectorAll('[data-jt-ph-orn]').forEach((btn) => {
    if (!once(btn, 'jtBound')) return;
    btn.addEventListener('click', () => {
      if (!reduceMotion.matches) restartClass(btn, 'is-spinning', 950);
      confettiAt(btn);
    });
  });
}

/* Questions: open or close them all at once */
function initFaq(scope) {
  scope.querySelectorAll('[data-jt-faq]').forEach((list) => {
    if (!once(list, 'jtBound')) return;
    const btn = list.querySelector('[data-jt-faq-all]');
    const items = [...list.querySelectorAll('details')];
    if (!btn) return;
    const sync = () => {
      const allOpen = items.every((d) => d.open);
      btn.setAttribute('aria-pressed', String(allOpen));
      btn.querySelector('[data-open-label]').hidden = allOpen;
      btn.querySelector('[data-close-label]').hidden = !allOpen;
    };
    btn.addEventListener('click', () => {
      const open = !items.every((d) => d.open);
      items.forEach((d) => (d.open = open));
      sync();
    });
    items.forEach((d) => d.addEventListener('toggle', sync));
    sync();
  });
}

/* Forms: friendly checks before sending, link prefill, counters, and a happy thank-you */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function fieldOf(el) {
  return el.closest('.jt-field');
}

function setError(field, msg) {
  if (!field) return;
  let err = field.querySelector('.jt-field__error');
  if (!msg) {
    err?.remove();
    field.querySelectorAll('input, select, textarea').forEach((i) => i.removeAttribute('aria-invalid'));
    return;
  }
  if (!err) {
    err = document.createElement('p');
    err.className = 'jt-field__error';
    err.id = `jt-err-${Math.random().toString(36).slice(2, 8)}`;
    field.appendChild(err);
  }
  err.textContent = msg;
  field.querySelectorAll('input:not([type="hidden"]), select, textarea').forEach((i) => {
    i.setAttribute('aria-invalid', 'true');
    i.setAttribute('aria-describedby', err.id);
  });
}

function checkField(el) {
  const field = fieldOf(el);
  if (!field) return true;
  if (el.type === 'radio') {
    const group = field.querySelectorAll(`input[type="radio"][name="${CSS.escape(el.name)}"]`);
    const required = [...group].some((r) => r.required);
    const ok = !required || [...group].some((r) => r.checked);
    setError(field, ok ? '' : 'Pick one so we know.');
    return ok;
  }
  const v = (el.value || '').trim();
  let msg = '';
  if (el.required && !v) msg = el.tagName === 'SELECT' ? 'Pick one so we know.' : 'Please fill this in.';
  else if (v && el.type === 'email' && !EMAIL.test(v)) msg = 'That email looks off. Check for typos?';
  else if (v && el.type === 'url' && !/^(https?:\/\/)?[^\s.]+\.[^\s]{2,}/i.test(v)) msg = 'Paste the full link, like drive.google.com/...';
  else if (v && el.type === 'number' && Number(v) < 1) msg = 'Use a number of 1 or more.';
  else if (v && el.type === 'date' && el.min && v < el.min) msg = 'Pick a date from today on.';
  setError(field, msg);
  return !msg;
}

function prefill(form) {
  const params = new URLSearchParams(location.search);
  form.querySelectorAll('[data-jt-param]').forEach((el) => {
    const want = params.get(el.dataset.jtParam);
    if (!want) return;
    const w = want.trim().toLowerCase();
    if (el.tagName === 'SELECT') {
      const opt = [...el.options].find((o) => o.value.toLowerCase() === w || o.value.toLowerCase().startsWith(w));
      if (opt) el.value = opt.value;
    } else if (el.getAttribute('role') === 'radiogroup') {
      const r = [...el.querySelectorAll('input[type="radio"]')].find(
        (i) => i.value.toLowerCase() === w || i.value.toLowerCase().startsWith(w)
      );
      if (r) r.checked = true;
    } else {
      el.value = want;
    }
  });
}

function initForms(scope) {
  scope.querySelectorAll('[data-jt-form] form').forEach((form) => {
    if (!once(form, 'jtBound')) return;
    form.noValidate = true;
    prefill(form);

    const today = todayLocal();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    form.querySelectorAll('input[data-jt-future]').forEach((i) => (i.min = iso));

    form.querySelectorAll('textarea[data-jt-count]').forEach((ta) => {
      const max = Number(ta.getAttribute('maxlength')) || 0;
      const out = document.createElement('p');
      out.className = 'jt-field__count';
      out.setAttribute('aria-hidden', 'true');
      const upd = () => (out.textContent = max ? `${ta.value.length} / ${max}` : String(ta.value.length));
      ta.after(out);
      ta.addEventListener('input', upd);
      upd();
    });

    const controls = () => [...form.querySelectorAll('input:not([type="hidden"]), select, textarea')];
    controls().forEach((el) => {
      const evt = el.type === 'radio' || el.tagName === 'SELECT' ? 'change' : 'blur';
      el.addEventListener(evt, () => {
        if (el.type === 'radio' || el.value.trim() || el.hasAttribute('aria-invalid')) checkField(el);
      });
      el.addEventListener('input', () => {
        if (el.hasAttribute('aria-invalid')) checkField(el);
      });
    });

    form.addEventListener('submit', (e) => {
      let first = null;
      const seen = new Set();
      controls().forEach((el) => {
        if (el.type === 'radio') {
          if (seen.has(el.name)) return;
          seen.add(el.name);
        }
        if (!checkField(el) && !first) first = el;
      });
      if (first) {
        e.preventDefault();
        first.focus({ preventScroll: true });
        first.closest('.jt-field')?.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
        return;
      }
      const btn = form.querySelector('[data-jt-form-submit]');
      if (btn) {
        btn.classList.add('is-busy');
        const label = btn.querySelector('span');
        if (label) label.textContent = 'Sending...';
      }
    });
  });

  scope.querySelectorAll('[data-jt-form-done]').forEach((done) => {
    if (!once(done, 'jtBound')) return;
    done.scrollIntoView({ behavior: 'auto', block: 'center' });
    done.focus({ preventScroll: true });
    setTimeout(() => confettiAt(done.querySelector('.jt-form__done-icon')), 350);
  });
  scope.querySelectorAll('[data-jt-form-errors]').forEach((box) => {
    if (!once(box, 'jtBound')) return;
    box.scrollIntoView({ behavior: 'auto', block: 'center' });
  });
}

/* Events: friendly countdown labels */
function initEvents(scope) {
  const today = todayLocal();
  scope.querySelectorAll('[data-jt-event-date]').forEach((card) => {
    const label = card.querySelector('[data-jt-evl-when]');
    const start = parseDay(card.dataset.jtEventDate);
    if (!label || !start) return;
    const end = parseDay(card.dataset.jtEventEnd) || start;
    const days = Math.round((start - today) / 86400000);
    let text = '';
    if (days <= 0 && today <= end) text = days === 0 ? 'Today!' : 'Happening now!';
    else if (days === 1) text = 'Tomorrow!';
    else if (days > 1 && days < 7) text = `In ${days} days`;
    else if (days >= 7 && days < 14) text = 'Next week';
    else if (days >= 14 && days < 60) text = `In ${Math.round(days / 7)} weeks`;
    else if (days >= 60) text = `In ${Math.round(days / 30)} months`;
    if (!text) return;
    label.textContent = text;
    label.hidden = false;
    label.classList.toggle('is-soon', days <= 3);
  });
}

/* Little machines that print, engrave, and recolor */
function whenVisible(el, fn) {
  if (!('IntersectionObserver' in window)) return fn();
  const io = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        fn();
      }
    },
    { threshold: 0.35 }
  );
  io.observe(el);
}

function initPrinter(svg, fig) {
  const layers = [...svg.querySelectorAll('.jt-art__layer')];
  const gantry = svg.querySelector('[data-jt-art-gantry]');
  const head = svg.querySelector('[data-jt-art-head]');
  const count = svg.querySelector('[data-jt-art-count]');
  const step = 230;
  let timer = 0;
  let run = 0;

  const showAll = () => {
    layers.forEach((l) => l.classList.add('is-on'));
    svg.classList.add('is-done');
    if (count) count.textContent = 'DONE!';
  };

  const reset = () => {
    clearTimeout(timer);
    layers.forEach((l) => l.classList.remove('is-on'));
    svg.classList.remove('is-done', 'is-printing');
  };

  const start = () => {
    reset();
    if (reduceMotion.matches) return showAll();
    const my = ++run;
    svg.classList.add('is-printing');
    svg.style.setProperty('--step', `${step}ms`);
    let i = 0;
    const tick = () => {
      if (my !== run) return;
      if (i >= layers.length) {
        svg.classList.remove('is-printing');
        svg.classList.add('is-done');
        if (count) count.textContent = 'DONE!';
        gantry.style.transform = `translate(0px, ${Number(layers[layers.length - 1].getAttribute('y')) - 18}px)`;
        head.style.transform = 'translate(160px, 0px)';
        timer = setTimeout(() => {
          if (my === run) start();
        }, 3200);
        return;
      }
      const l = layers[i];
      const x = Number(l.getAttribute('x'));
      const w = Number(l.getAttribute('width'));
      const y = Number(l.getAttribute('y'));
      gantry.style.transform = `translate(0px, ${y}px)`;
      head.style.transform = `translate(${i % 2 ? x : x + w}px, 0px)`;
      l.classList.add('is-on');
      if (count) count.textContent = `LAYER ${i + 1}/${layers.length}`;
      i += 1;
      timer = setTimeout(tick, step);
    };
    gantry.style.transform = `translate(0px, ${Number(layers[0].getAttribute('y'))}px)`;
    timer = setTimeout(tick, 300);
  };

  fig.querySelector('[data-jt-art-replay]')?.addEventListener('click', start);
  svg.addEventListener('click', start);
  whenVisible(svg, start);
}

function initLaser(svg, fig) {
  const path = svg.querySelector('[data-jt-art-path]');
  const glow = svg.querySelector('.jt-art__burn-glow');
  const head = svg.querySelector('[data-jt-art-head]');
  const rail = svg.querySelector('[data-jt-art-rail]');
  const count = svg.querySelector('[data-jt-art-count]');
  if (!path || !head) return;
  const len = path.getTotalLength();
  path.style.strokeDasharray = `${len}`;
  let raf = 0;
  let run = 0;
  let timer = 0;

  const place = (d) => {
    const p = path.getPointAtLength(Math.min(len, Math.max(0, d)));
    head.setAttribute('transform', `translate(${p.x.toFixed(1)} ${p.y.toFixed(1)})`);
    rail.setAttribute('transform', `translate(0 ${p.y.toFixed(1)})`);
  };

  const finish = () => {
    path.style.strokeDashoffset = '0';
    svg.classList.remove('is-engraving');
    svg.classList.add('is-done');
    if (count) count.textContent = 'ALL DONE!';
  };

  const start = () => {
    cancelAnimationFrame(raf);
    clearTimeout(timer);
    svg.classList.remove('is-done');
    if (reduceMotion.matches) {
      place(len);
      return finish();
    }
    const my = ++run;
    const dur = 5200;
    const t0 = performance.now();
    svg.classList.add('is-engraving');
    if (glow) glow.style.opacity = '0';
    const frame = (now) => {
      if (my !== run) return;
      const k = Math.min(1, (now - t0) / dur);
      const d = len * k;
      path.style.strokeDashoffset = String(len - d);
      place(d);
      if (count) count.textContent = `ENGRAVING ${Math.round(k * 100)}%`;
      if (k < 1) raf = requestAnimationFrame(frame);
      else {
        if (glow) glow.style.opacity = '';
        finish();
        timer = setTimeout(() => {
          if (my === run) start();
        }, 3500);
      }
    };
    path.style.strokeDashoffset = String(len);
    raf = requestAnimationFrame(frame);
  };

  path.style.strokeDashoffset = String(len);
  place(0);
  fig.querySelector('[data-jt-art-replay]')?.addEventListener('click', start);
  svg.addEventListener('click', start);
  whenVisible(svg, start);
}

function initShirt(svg, fig) {
  const swatches = [...fig.querySelectorAll('[data-shirt]')];
  let idx = 0;
  const pick = (i) => {
    idx = (i + swatches.length) % swatches.length;
    const sw = swatches[idx];
    fig.style.setProperty('--shirt', sw.dataset.shirt);
    fig.style.setProperty('--ink', sw.dataset.ink);
    swatches.forEach((b, j) => b.setAttribute('aria-checked', String(j === idx)));
    if (!reduceMotion.matches) restartClass(svg, 'is-sweep', 750);
  };
  swatches.forEach((b, i) => b.addEventListener('click', () => pick(i)));
  svg.addEventListener('click', () => pick(idx + 1));
}

function initMachines(scope) {
  scope.querySelectorAll('[data-jt-machine]').forEach((fig) => {
    if (!once(fig, 'jtBound')) return;
    const svg = fig.querySelector('[data-jt-art]');
    if (!svg) return;
    const kind = svg.dataset.jtArt;
    if (kind === 'printer') initPrinter(svg, fig);
    else if (kind === 'laser') initLaser(svg, fig);
    else if (kind === 'shirt') initShirt(svg, fig);
  });
}

function boot(scope = document) {
  initOrnaments(scope);
  initFaq(scope);
  initForms(scope);
  initEvents(scope);
  initMachines(scope);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
else boot();
document.addEventListener('shopify:section:load', (e) => boot(e.target));
